<template>
    <div class="page-home">
        <div class="page-home__container container">
            <LazyHydrate when-idle>
                <MainSlider :slides="mainBannerSlides" :title="seo.title" />
            </LazyHydrate>

            <CategoriesShowcase :categories="mainCategories" />

            <div ref="productSliders">
                <LazyHydrate when-visible>
                    <ProductsSlider
                        v-if="firstProductsSlider?.products?.length"
                        :title="firstProductsSlider.title"
                        :products="firstProductsSlider.products"
                        :analyticProducts="firstProductsSlider?.analyticProducts"
                    />
                </LazyHydrate>
            </div>

            <template v-for="(slider, index) in restOfProductSliders">
                <LazyHydrate when-visible>
                    <ProductsSlider
                        v-if="slider?.products?.length"
                        :key="index"
                        :title="slider.title"
                        :products="slider.products"
                        :analyticProducts="slider?.analyticProducts"
                    />
                </LazyHydrate>
            </template>

            <div ref="servicesComposition">
                <LazyHydrate when-visible>
                    <ServicesComposition />
                </LazyHydrate>
            </div>

            <div ref="infoSectionNumber">
                <LazyHydrate when-visible>
                    <InfoSection
                        v-if="advantages"
                        :titleData="{
                            title: advantages.title,
                            title_background_color: null,
                            title_text_color: null
                        }"
                        :numericBlock="{
                            id: 1,
                            numeric_block_items: advantages.items
                        }"
                    />
                </LazyHydrate>
            </div>

            <div ref="blogPreview">
                <LazyHydrate when-visible>
                    <BlogPreview />
                </LazyHydrate>
            </div>
            <div ref="popularQuestions">
                <LazyHydrate when-visible>
                    <PopularQuestions />
                </LazyHydrate>
            </div>
            <LazyHydrate when-visible>
                <StoresLocations />
            </LazyHydrate>
        </div>
    </div>
</template>

<script>
import seo from '~/mixins/seo';
import { urlHome } from '~/api/seo';
import LazyHydrate from 'vue-lazy-hydration';
import { mapGetters } from 'vuex';

import MainSlider from './index/components/MainSlider';
import StoresLocations from '~/themes/novaporta/components/modular/StoresLocations';
import ProductsSlider from '@/themes/novaporta/components/reusable/ProductsSlider';

import { createServicesCompositionItem } from '@/themes/novaporta/components/reusable/ServicesComposition/types';
import { createBlogItem } from '@/themes/novaporta/components/reusable/BlogPreview/types';
import { createSliderItem } from './index/components/MainSlider/types';
import {
    createCategoryShowcaseItem,
    createSubCategoryShowcaseItem
} from '@/themes/novaporta/components/reusable/CategoriesShowcase/types';
import { createProductAgent } from '@/themes/novaporta/components/reusable/ProductAgent/types';
import {
    createPopularQuestionsItem,
    createPopularQuestions
} from '@/themes/novaporta/components/reusable/PopularQuestions/types';

const productSliderDataPlaceholder = {
    title: '',
    products: [null, null, null, null, null, null] // Null array is used to show skeleton preloaders
};

export default {
    name: 'PageHome',
    mixins: [seo],
    data() {
        return {
            isLoadSliders: true,

            mainBannerSlides: [],
            mainCategories: [],
            blogItems: [],
            popularQuestions: null,
            blogPreviewObserver: null,
            advantages: null,
            sliders: [],
            services: [],

            popularQuestionsObserver: null,
            infoSectionNumberObserver: null,
            servicesCompositionObserver: null,
            productSlidersObserver: null
        };
    },
    computed: {
        ...mapGetters({
            globalSettings: 'global/globalSettings'
        }),
        firstProductsSlider() {
            return this.sliders[0] || (this.isLoadSliders ? productSliderDataPlaceholder : []);
        },
        restOfProductSliders() {
            return this.sliders.length
                ? this.sliders.filter((s, idx) => idx !== 0)
                : this.isLoadSliders
                ? [productSliderDataPlaceholder]
                : [];
        }
    },
    components: {
        LazyHydrate,
        MainSlider,
        ProductsSlider,
        AskUs: () => import('@/themes/novaporta/components/reusable/AskUs'),
        CategoriesShowcase: () => import('@/themes/novaporta/components/reusable/CategoriesShowcase'),
        ProductAgent: () => import('@/themes/novaporta/components/reusable/ProductAgent'),
        InfoSection: () => import('@/themes/novaporta/components/reusable/InfoSection'),
        BlogPreview: () => import('@/themes/novaporta/components/reusable/BlogPreview'),
        ServicesComposition: () => import('@/themes/novaporta/components/reusable/ServicesComposition'),
        OfferRequest: () => import('@/themes/novaporta/components/reusable/OfferRequest'),
        PopularQuestions: () => import('@/themes/novaporta/components/reusable/PopularQuestions'),

        StoresLocations: () => import('@/themes/novaporta/components/modular/StoresLocations')
    },
    methods: {
        // TODO - Delete these observer methods after testing and refactoring
        popularQuestionsObserverInit() {
            this.popularQuestionsObserver = new IntersectionObserver(this.fetchPopularQuestions, {
                root: null,
                rootMargin: '0px',
                threshold: 0.5
            });
            this.popularQuestionsObserver.observe(this.$refs.popularQuestions);
        },
        infoSectionNumberObserverInit() {
            this.infoSectionNumberObserver = new IntersectionObserver(this.fetchInfoSectionNumber, {
                root: null,
                rootMargin: '0px',
                threshold: 0.5
            });
            this.infoSectionNumberObserver.observe(this.$refs.infoSectionNumber);
        },
        servicesCompositionObserverInit() {
            this.servicesCompositionObserver = new IntersectionObserver(this.fetchServicesComposition, {
                root: null,
                rootMargin: '0px',
                threshold: 0.5
            });
            this.servicesCompositionObserver.observe(this.$refs.servicesComposition);
        },
        productSlidersObserverInit() {
            this.productSlidersObserver = new IntersectionObserver(this.fetchProductSliders, {
                root: null,
                rootMargin: '0px',
                threshold: 0.5
            });
            this.productSlidersObserver.observe(this.$refs.productSliders);
        },
        async fetchPopularQuestions(entries) {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    const { data } = await this.$api.home.getPopularQuestions();

                    this.popularQuestions = createPopularQuestions({
                        id: data.id,
                        name: data.name,
                        questions: data.faq_items.map(child => {
                            return createPopularQuestionsItem({
                                id: child.id,
                                answer: child.answer,
                                question: child.question
                            });
                        })
                    });

                    this.popularQuestionsObserver.disconnect();
                }
            }
        },
        async fetchBlogItems(entries) {
            // for (const entry of entries) {
            //     if (entry.isIntersecting) {
            try {
                const { data } = await this.$api.article.getArticle('top');

                this.blogItems = [...data.new, ...data.popular].map(item => {
                    return createBlogItem({
                        title: item.title,
                        img: item.img,
                        date: item.publishDate,
                        url: `/articles/${item.url}`
                    });
                });
                this.blogPreviewObserver.disconnect();
            } catch {
                console.error('Failed to fetch articles:', error);
            }
            //     }
            // }
        },
        async fetchMainSliderData() {
            const { data } = await this.$api.home.getMainSlider();

            this.mainBannerSlides = data.map(slide => {
                return createSliderItem({
                    title: slide.title,
                    description: slide.description,
                    btnText: slide.button_name ? slide.button_name : this.$t('globalActionOrderNow'), // Should come from Request data
                    imageDesktop: slide.images.desktop,
                    imageTablet: slide.images.tablet,
                    imageMobile: slide.images.mobile,
                    url: slide.productUrl,
                    autoplay: slide.autoplay
                });
            });
        },
        async fetchCategories() {
            try {
                const { data } = await this.$api.home.getTopCategories();

                this.mainCategories = data?.categories?.map(category => {
                    return createCategoryShowcaseItem({
                        mainTitle: category.title,
                        mainImage: category?.img,
                        mainUrl: category.link
                    });
                });
            } catch (error) {
                console.error('Failed to fetch categories:', error);
            }
        },
        async fetchServicesComposition(entries) {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    try {
                        const {
                            data: { service_items }
                        } = await this.$api.home.getServices();
                        this.services = service_items
                            .map(item => {
                                if (item.name) {
                                    return createServicesCompositionItem({
                                        id: item.id,
                                        title: item.name,
                                        description: item.description,
                                        image: item.image,
                                        icon: item.icon,
                                        link: item.link
                                    });
                                }
                            })
                            .filter(item => item);

                        this.servicesCompositionObserver.disconnect();
                    } catch {
                        console.error('Failed to fetch services:', error);
                    }
                }
            }
        },
        async fetchInfoSectionNumber(entries) {
            // for (const entry of entries) {
            //     if (entry.isIntersecting) {
            try {
                const { data } = await this.$api.advantages.getAdvantages();
                const baseUrl = this.$config.apiBaseUrl;

                this.advantages = {
                    title: data.title,
                    items: data.advantages.map(item => {
                        return {
                            id: item.order,
                            text: item.title,
                            icon: `${baseUrl}${item.icon}` // Not full path, gluing
                        };
                    })
                };

                // this.infoSectionNumberObserver.disconnect();
            } catch (error) {
                console.error('Failed to fetch infoSectionNumber:', error);
            }
            //     }
            // }
        },
        async fetchProductSliders(entries) {
            // for (const entry of entries) {
            //     if (entry.isIntersecting) {
            this.isLoadSliders = true;
            try {
                const { data } = await this.$api.home.getProductSliders();

                this.sliders = data.map(slider => {
                    return {
                        title: slider.carouselName,
                        analyticProducts: slider.products,
                        products: slider.products.map(product => {
                            return createProductAgent({
                                active: product.active,
                                available: product.available,
                                id: product?.productId,
                                name: product.title,
                                price: Number(product.price),
                                slug: product?.url,
                                img: product.img,
                                oldPrice: Number(product.old_price),
                                badges: product.badges_products.map(item => {
                                    return {
                                        name: item.name,
                                        color: item.text_color,
                                        backgroundColor: item.background_color
                                    };
                                }),
                                slides: product?.images
                                    ? product?.images?.map(s => {
                                          return { img: s };
                                      })
                                    : [],
                                isOnSale: Number(product.old_price) ? true : false
                            });
                        })
                    };
                });
                // this.productSlidersObserver.disconnect();
            } catch (error) {
                console.error('Failed to fetch sliders:', error);
            } finally {
                this.isLoadSliders = false;
            }
            //     }
            // }
        }
    },
    // head() {
    //     const micromarking = this.globalSettings.micro_pagination_settings;
    //     return {
    //         script: micromarking
    //             ? [
    //                   {
    //                       type: 'application/ld+json',
    //                       json: {
    //                           '@context': 'https://schema.org',
    //                           '@type': 'Organization',
    //                           url: micromarking.url,
    //                           name: process.env.PUBLIC_NAME,
    //                           sameAs: micromarking.optional_urls,
    //                           description: micromarking.description,
    //                           address: {
    //                               '@type': 'PostalAddress',
    //                               ...micromarking.address
    //                           },
    //                           contactPoint: {
    //                               '@type': 'ContactPoint',
    //                               ...micromarking.contact_point
    //                           }
    //                       }
    //                   }
    //               ]
    //             : []
    //     };
    // },
    async asyncData({ $axios }) {
        const seo = await $axios.$get(urlHome);
        return { fetchedSeo: seo };
    },
    created() {
        if (this.fetchedSeo) {
            this.setDataSEO(this.fetchedSeo);
        }
    },
    async fetch() {
        // await this.fetchSEO(urlHome);
        await this.fetchMainSliderData();
        await this.fetchProductSliders();
        await this.fetchCategories();
        if (this.globalSettings?.advantages_pages?.find(item => 'main_page' === item)) {
            await this.fetchInfoSectionNumber();
        }
    },
    mounted() {
        // this.blogPreviewObserver = new IntersectionObserver(this.fetchBlogItems, {
        //     root: null,
        //     rootMargin: '0px',
        //     threshold: 0.5
        // });
        // this.blogPreviewObserver.observe(this.$refs.blogPreview);
        // this.popularQuestionsObserverInit();
        // this.infoSectionNumberObserverInit();
        // this.servicesCompositionObserverInit();
        // this.productSlidersObserverInit();
    }
};
</script>
